/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState, useRef } from "react";
import { child, push, ref, update, get } from "firebase/database";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import ListState from "../../../../components/ListState/ListState";
import { rtdb } from "../../../../firebase";
import { ITelehealthCallParticipants } from "../../../../utils/types";

interface JitsiComponentProps {
    roomName: string;
    providerEmail: string;
    providerName: string;
    token: string;
    userId: string;
}

declare global {
    interface Window {
        JitsiMeetExternalAPI: new (domain: string, options: any) => any;
    }
}

export default function JitsiDirectComponent({
    roomName,
    providerEmail,
    providerName,
    token,
    userId,
}: JitsiComponentProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const apiRef = useRef<any>(null);
    const navigate = useNavigate();
    const domain = import.meta.env.VITE_MEDCON_SERVER_URL;

    // Initialize Jitsi meeting
    useEffect(() => {
        const handleUpdateCallParticipantsCollection = async (
            status: "active" | "inactive"
        ) => {
            const collection = await get(
                child(ref(rtdb), `/telehealth_calls/${roomName}`)
            );

            if (collection.exists()) {
                const keys = Object.keys(collection.val());
                let partcipantsArray: ITelehealthCallParticipants[] = [];

                keys.forEach((p) => {
                    partcipantsArray = [
                        {
                            ...collection.val()[p],
                        },
                        ...partcipantsArray,
                    ];
                });

                const userInParticipantsArrayDetails = partcipantsArray.find(
                    (participant) => participant.user_id === userId
                );

                if (userInParticipantsArrayDetails) {
                    try {
                        await update(
                            ref(
                                rtdb,
                                `/telehealth_calls/${roomName}/${userInParticipantsArrayDetails.id}`
                            ),
                            {
                                ...userInParticipantsArrayDetails,
                                status,
                                updated_at: new Date().toISOString(),
                            }
                        );
                    } catch (err) {
                        Sentry.captureException(
                            `Could not add ${providerName} to call - ${roomName} collection`
                        );
                    }
                } else {
                    const newCollectionKey = push(
                        child(ref(rtdb), `/telehealth_calls/${roomName}`)
                    ).key;

                    try {
                        await update(
                            ref(
                                rtdb,
                                `/telehealth_calls/${roomName}/${newCollectionKey}`
                            ),
                            {
                                name: providerName,
                                user_id: userId,
                                role: "provider",
                                status,
                                id: newCollectionKey,
                                updated_at: new Date().toISOString(),
                                session_history_id: roomName,
                            }
                        );
                    } catch (err) {
                        Sentry.captureException(
                            `Could not add ${providerName} to call - ${roomName} collection`
                        );
                    }
                }
            } else {
                const newCollectionKey = push(
                    child(ref(rtdb), `/telehealth_calls/${roomName}`)
                ).key;

                try {
                    await update(
                        ref(
                            rtdb,
                            `/telehealth_calls/${roomName}/${newCollectionKey}`
                        ),
                        {
                            name: providerName,
                            user_id: userId,
                            role: "provider",
                            status,
                            id: newCollectionKey,
                            updated_at: new Date().toISOString(),
                            session_history_id: roomName,
                        }
                    );
                } catch (err) {
                    Sentry.captureException(
                        `Could not add ${providerName} to call - ${roomName} collection`
                    );
                }
            }
        };
        const setupEventListeners = () => {
            if (!apiRef.current) return;

            const refreshParticipantsPanel = () => {
                apiRef.current.executeCommand("refreshParticipantsPane");
            };

            // Core participant events
            const events = [
                "participantJoined",
                "participantLeft",
                "displayNameChange",
                "videoMuteStatusChanged",
                "audioMuteStatusChanged",
                "participantRoleChanged",
                "trackAdded",
                "trackRemoved",
            ];

            events.forEach((event) => {
                apiRef.current.on(event, refreshParticipantsPanel);
            });

            apiRef.current.on("videoConferenceJoined", () => {
                handleUpdateCallParticipantsCollection("active");
            });

            // Navigation events
            apiRef.current.on("readyToClose", () => {
                handleUpdateCallParticipantsCollection("inactive");

                apiRef.current?.dispose();

                navigate("/");
            });
        };

        const initJitsi = () => {
            try {
                const options = {
                    roomName,
                    parentNode: document.getElementById("jitsi-container"),
                    jwt: token,
                    userInfo: {
                        displayName: providerName,
                        email: providerEmail,
                    },
                    configOverwrite: {
                        disableDeepLinking: true,
                        enableDisplayNameInStats: true,
                        enableFeatures: "participants-pane",
                        hideConferenceSubject: true,
                        startWithAudioMuted: false,
                        startWithVideoMuted: true,
                        prejoinPageEnabled: false,
                        disableInviteFunctions: true,
                        enableClosePage: false,
                        enableWelcomePage: false,
                        p2p: {
                            enabled: true,
                            stunServers: [
                                {
                                    urls:
                                        import.meta.env
                                            .VITE_MEDCON_STUN_SERVER_URL ||
                                        "stun:stun.l.google.com:19302",
                                },
                            ],
                        },
                        e2eping: {
                            pingInterval: -1,
                            analyticsInterval: -1,
                        },
                    },
                    interfaceConfigOverwrite: {
                        RECENT_LIST_ENABLED: true,
                        DISABLE_PRESENCE_STATUS: false,
                        DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                        SHOW_CHROME_EXTENSION_BANNER: false,
                        MOBILE_APP_PROMO: false,
                        HIDE_INVITE_MORE_HEADER: true,
                        DISABLE_VIDEO_BACKGROUND: true,
                        filmStripOnly: false,
                        disablePolls: true,
                        disableRecording: true,
                        disableFeedback: true,
                        disableKickOut: true,
                        disableInviteFunctions: true,
                        disableChat: true,
                        disableRaiseHand: true,
                        disableVideoQualityLabel: true,
                        disableVideoBackground: true,
                        SHOW_BRAND_WATERMARK: false,
                        SHOW_WATERMARK_FOR_GUESTS: false,
                        SHOW_POWERED_BY: false,
                    },
                    onload: () => {
                        setIsLoading(false);
                    },
                };

                apiRef.current = new window.JitsiMeetExternalAPI(
                    domain,
                    options
                );

                // Set up event listeners
                setupEventListeners();
            } catch (err) {
                Sentry.captureException(err);
                setError("Failed to initialize meeting");
            }
        };

        if (!window.JitsiMeetExternalAPI) {
            const script = document.createElement("script");
            script.src = `https://${domain}/external_api.js`;
            script.async = true;
            script.onload = initJitsi;
            script.onerror = (err) => {
                Sentry.captureException(err);
                setError("Failed to load Jitsi API");
            };
            document.body.appendChild(script);
        } else {
            initJitsi();
        }

        return () => {
            apiRef.current?.dispose();
        };
    }, [
        domain,
        token,
        navigate,
        roomName,
        providerName,
        providerEmail,
        userId,
    ]);

    return (
        <>
            {isLoading ? (
                <div className="justify-center h-[92vh] flex items-center">
                    <span className="mid-loader" />
                </div>
            ) : null}
            {!isLoading && error ? (
                <div className="h-full text-sm font-medium flex items-center justify-center">
                    <ListState
                        isError
                        errorMsg="Could not load meeting at this time"
                        stateHelperText="Try reloading this page to solve this issue"
                        context="general"
                        cardClass="h-[92vh]"
                    />
                </div>
            ) : null}
            <div
                id="jitsi-container"
                className="h-full w-full"
                style={{ display: isLoading ? "none" : "block" }}
            />
        </>
    );
}
